import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import { queryKeys } from "../lib/queryKeys";
import type {
  AuthoringRun,
  AuthoringStartRequest,
  Icp,
  IcpPromptVersion,
} from "../lib/types";

interface StartResponse {
  run: AuthoringRun;
}

interface GetResponse {
  run: AuthoringRun;
  prompt_version: IcpPromptVersion | null;
}

interface ListResponse {
  runs: AuthoringRun[];
  total: number;
}

interface PromoteResponse {
  version: IcpPromptVersion;
  icp: Icp;
}

const callStart = httpsCallable<AuthoringStartRequest, StartResponse>(
  functions,
  "cf_authoring_start",
);
const callGet = httpsCallable<{ run_id: string }, GetResponse>(
  functions,
  "cf_authoring_get",
);
const callList = httpsCallable<
  { icp_name?: string | null; limit?: number; offset?: number },
  ListResponse
>(functions, "cf_authoring_list");
const callDelete = httpsCallable<{ run_id: string }, { ok: true }>(
  functions,
  "cf_authoring_delete",
);
const callPromote = httpsCallable<{ version_id: number }, PromoteResponse>(
  functions,
  "cf_authoring_promote",
);

const AUTHORING_RUN_KEY = (runId: string) => ["authoring-run", runId] as const;
const AUTHORING_RUNS_KEY = (icpName: string | null) =>
  ["authoring-runs", icpName] as const;

const ACTIVE_STATUSES = new Set(["queued", "enriching", "iterating"]);

export function useStartAuthoringRun() {
  const qc = useQueryClient();
  return useMutation<AuthoringRun, Error, AuthoringStartRequest>({
    mutationFn: async (body) => {
      const res = await callStart(body);
      return res.data.run;
    },
    onSuccess: (run) => {
      qc.setQueryData<GetResponse>(AUTHORING_RUN_KEY(run.id), (prev) => ({
        run,
        prompt_version: prev?.prompt_version ?? null,
      }));
      qc.invalidateQueries({ queryKey: AUTHORING_RUNS_KEY(run.icp_name) });
      qc.invalidateQueries({ queryKey: AUTHORING_RUNS_KEY(null) });
    },
  });
}

export function useAuthoringRun(
  runId: string | null,
  options?: { refetchInterval?: number | false },
) {
  const interval = options?.refetchInterval;
  return useQuery<GetResponse, Error>({
    queryKey: AUTHORING_RUN_KEY(runId ?? ""),
    queryFn: async () => {
      if (!runId) throw new Error("runId is required");
      const res = await callGet({ run_id: runId });
      return res.data;
    },
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      if (interval === false) return false;
      const data = query.state.data as GetResponse | undefined;
      if (!data) return interval ?? 3000;
      return ACTIVE_STATUSES.has(data.run.status) ? interval ?? 3000 : false;
    },
  } satisfies UseQueryOptions<GetResponse, Error>);
}

export interface AuthoringRunsPage {
  runs: AuthoringRun[];
  total: number;
}

export function useAuthoringRuns(
  icpName: string | null,
  options?: { page?: number; pageSize?: number },
) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 10;
  return useQuery<AuthoringRunsPage, Error>({
    queryKey: [...AUTHORING_RUNS_KEY(icpName), page, pageSize],
    queryFn: async () => {
      const res = await callList({
        icp_name: icpName ?? null,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      return { runs: res.data.runs, total: res.data.total };
    },
    staleTime: 15_000,
  });
}

export function useDeleteAuthoringRun() {
  const qc = useQueryClient();
  return useMutation<void, Error, { run_id: string; icp_name: string | null }>({
    mutationFn: async ({ run_id }) => {
      await callDelete({ run_id });
    },
    onSuccess: (_void, { run_id, icp_name }) => {
      qc.removeQueries({ queryKey: AUTHORING_RUN_KEY(run_id) });
      qc.invalidateQueries({ queryKey: AUTHORING_RUNS_KEY(icp_name) });
      qc.invalidateQueries({ queryKey: AUTHORING_RUNS_KEY(null) });
    },
  });
}

export function usePromotePromptVersion() {
  const qc = useQueryClient();
  return useMutation<PromoteResponse, Error, { version_id: number }>({
    mutationFn: async (body) => {
      const res = await callPromote(body);
      return res.data;
    },
    onSuccess: ({ icp, version }) => {
      qc.setQueryData<Icp[]>(queryKeys.icps.all, (prev) =>
        prev ? prev.map((p) => (p.name === icp.name ? icp : p)) : prev,
      );
      if (version.source_run_id) {
        qc.invalidateQueries({ queryKey: AUTHORING_RUN_KEY(version.source_run_id) });
      }
      qc.invalidateQueries({ queryKey: AUTHORING_RUNS_KEY(icp.name) });
    },
  });
}
